import type { Locale } from "@core/schema/Locale.js";
import type {
	ProductAttributeGroupOfValue,
	ProductForCustomer,
	ProductVariantForCustomer,
} from "@core/schema/Product.js";
import type { SubcategoryForCustomer } from "@core/schema/Subcategory.js";
import { appName } from "@core/schema/names/appName.js";

/**
 * Builds an SEO-friendly alt text for product images. Pattern:
 *   "{product} – {subcategory} – {attribute values…} – {variant?} – Father's Coffee Roastery"
 *
 * Attribute values (e.g. color) are included whenever the product has any —
 * works for the existing Color type and any future ProductAttributeGroupType.
 * Variant label is included only when the gallery / card is bound to a specific
 * variant (product detail image, sized variant). For listing cards the variant
 * is omitted because the selected variant can change on hover.
 */
export function buildProductAlt(
	product: Pick<ProductForCustomer, "name">,
	subcategory: Pick<SubcategoryForCustomer, "name">,
	locale: Locale,
	attributes: ProductAttributeGroupOfValue[],
	variant?: Pick<ProductVariantForCustomer, "label">,
): string {
	const parts = [product.name[locale], subcategory.name[locale]];
	for (const attribute of attributes) {
		parts.push(attribute.value.label[locale]);
	}
	if (variant) {
		parts.push(variant.label[locale]);
	}
	parts.push(appName);
	return parts.join(" – ");
}
